/**
 * PATH: apps/quote/src/pages/templates-page/TemplatesPage.tsx
 * PURPOSE: Orchestrator for template management page
 */

import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Grid3X3, List, Loader2, AlertCircle, FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { quoteApi, type SpreadsheetTemplate, type TemplateCategory, type TemplateStatus } from '@/lib/quoteApi'
import TemplateCard from './TemplateCard'
import TemplateListRow from './TemplateListRow'
import CreateTemplateModal from './CreateTemplateModal'

const CATEGORY_OPTIONS: { value: TemplateCategory; label: string }[] = [
  { value: 'quote', label: 'Quote' },
  { value: 'variation', label: 'Variation' },
  { value: 'claim', label: 'Payment Claim' },
  { value: 'report', label: 'Report' },
  { value: 'other', label: 'Other' },
]

type ViewMode = 'grid' | 'list'

export default function TemplatesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<TemplateCategory | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<TemplateStatus | 'all'>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)

  const { data: templatesData, isLoading, error } = useQuery({
    queryKey: ['spreadsheet-templates', categoryFilter, statusFilter],
    queryFn: () => quoteApi.listTemplates(categoryFilter !== 'all' ? categoryFilter : undefined, statusFilter !== 'all' ? statusFilter : undefined),
  })

  const templates = templatesData?.results || []
  const filteredTemplates = templates.filter((t) => t.name.toLowerCase().includes(searchQuery.toLowerCase()) || (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase())))

  const createFromTemplateMutation = useMutation({
    mutationFn: async (template: SpreadsheetTemplate) => quoteApi.createQuote({ quote_type: 'quote', project_name: `Quote from ${template.name}`, template_id: template.id }),
    onSuccess: (quote) => { navigate(`/builder/${quote.id}`) },
  })

  const handleUseTemplate = useCallback((t: SpreadsheetTemplate) => { createFromTemplateMutation.mutate(t) }, [createFromTemplateMutation])
  const handleEdit = useCallback((t: SpreadsheetTemplate) => { navigate(`/templates/${t.id}`) }, [navigate])
  const handleDuplicate = useCallback(async (t: SpreadsheetTemplate) => { await quoteApi.createTemplate(`${t.name} (Copy)`, t.category, t.description); queryClient.invalidateQueries({ queryKey: ['spreadsheet-templates'] }) }, [queryClient])
  const handleArchive = useCallback(async (t: SpreadsheetTemplate) => { await quoteApi.archiveTemplate(t.id); queryClient.invalidateQueries({ queryKey: ['spreadsheet-templates'] }) }, [queryClient])

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Spreadsheet Templates</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Pre-configured Excel templates with named range bindings for quick quote creation</p>
          </div>
          <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors shadow-md">
            <Plus className="h-5 w-5" /> Create Template
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="text" placeholder="Search templates..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value as TemplateCategory | 'all')} className="px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white">
            <option value="all">All Categories</option>
            {CATEGORY_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as TemplateStatus | 'all')} className="px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          <div className="flex items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-1">
            <button onClick={() => setViewMode('grid')} className={cn('p-2 rounded', viewMode === 'grid' ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700')}><Grid3X3 className="h-4 w-4" /></button>
            <button onClick={() => setViewMode('list')} className={cn('p-2 rounded', viewMode === 'list' ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700')}><List className="h-4 w-4" /></button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-center"><AlertCircle className="h-12 w-12 text-red-500 mb-4" /><h3 className="text-lg font-semibold text-slate-900 dark:text-white">Failed to load templates</h3></div>
        ) : filteredTemplates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <FileText className="h-16 w-16 text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">No templates found</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{searchQuery ? 'Try adjusting your search' : 'Create your first template to get started'}</p>
            <button onClick={() => setShowCreateModal(true)} className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"><Plus className="h-4 w-4" /> Create Template</button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredTemplates.map((t) => <TemplateCard key={t.id} template={t} onUseTemplate={handleUseTemplate} onEdit={handleEdit} onDuplicate={handleDuplicate} onArchive={handleArchive} />)}
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900">
                <tr className="text-left text-sm text-slate-600 dark:text-slate-400">
                  {['Template', 'Category', 'Status', 'Bindings', 'Used', 'Version', 'Actions'].map(h => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}
                </tr>
              </thead>
              <tbody>{filteredTemplates.map((t) => <TemplateListRow key={t.id} template={t} onUseTemplate={handleUseTemplate} onEdit={handleEdit} />)}</tbody>
            </table>
          </div>
        )}

        {createFromTemplateMutation.isPending && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <p className="text-slate-900 dark:text-white font-medium">Creating quote from template...</p>
            </div>
          </div>
        )}
      </div>
      <CreateTemplateModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onCreated={() => queryClient.invalidateQueries({ queryKey: ['spreadsheet-templates'] })} />
    </div>
  )
}
